import { useState } from 'react';
import { useWorkspace } from './workspaceContext';
import { NavLink, useLocation } from 'react-router';
import { Menu } from 'lucide-react';
import { routeResources, type FeatureSection } from '@/app/registry';
import { sections } from '@/app/sections';
import { useAuth } from '@/auth/AuthProvider';
import { useNavTitle } from '@/i18n/useNavTitle';
import { useTranslation } from 'react-i18next';
import { IconButton, Popover, PopoverContent, PopoverTrigger, Sheet, Tooltip, cn, focusRing } from '@/ui';

/** The sections and menu routes the admin can read (hidden routes and empty sections dropped). */
export function useMenu(): FeatureSection[] {
  const { permissions } = useAuth();
  return sections
    .map((s) => ({ ...s, routes: s.routes.filter((r) => !r.hideInMenu && permissions.canViewAny(routeResources(r))) }))
    .filter((s) => s.routes.length > 0);
}

const item = cn(
  'flex h-8 items-center gap-2 rounded-md px-2.5 text-base text-ink no-underline transition-colors hover:bg-muted',
  'aria-[current=page]:bg-accent-soft aria-[current=page]:font-bold aria-[current=page]:text-accent',
  focusRing,
);

/** A collapsed section: its icon, opening a flyout of its screens. */
function RailSection({ section }: { section: FeatureSection }) {
  const workspace = useWorkspace();
  const title = useNavTitle();
  const Icon = section.icon;
  return (
    <Popover>
      <Tooltip content={title(section.title)} side="right">
        <PopoverTrigger
          aria-label={title(section.title)}
          className={cn('m-0 flex size-10 cursor-pointer items-center justify-center rounded-md border-0 bg-transparent p-0 text-ink-muted hover:bg-muted hover:text-ink', focusRing)}
        >
          <Icon size={18} aria-hidden />
        </PopoverTrigger>
      </Tooltip>
      <PopoverContent side="right" align="start" className="w-56 p-1.5">
        <div className="px-2.5 pb-1 pt-1 text-xs font-bold uppercase tracking-wide text-ink-muted">{title(section.title)}</div>
        {section.routes.map((r) => (
          <NavLink key={r.path} to={workspace?.href(r.path) ?? r.path} className={item}>
            <span className="truncate" title={title(r.title)}>
              {title(r.title)}
            </span>
          </NavLink>
        ))}
      </PopoverContent>
    </Popover>
  );
}

/** Every section with its screens (the expanded sidebar, and the phone drawer). */
function SectionList({ touch }: { touch?: boolean }) {
  const workspace = useWorkspace();
  const title = useNavTitle();
  const menu = useMenu();
  return menu.map((s) => {
    const Icon = s.icon;
    return (
      <section key={s.key} aria-labelledby={`nav-${s.key}`} className="mb-4 last:mb-0">
        <h2 id={`nav-${s.key}`} className="m-0 mb-1 flex items-center gap-2 px-2.5 font-sans text-xs font-bold uppercase tracking-wide text-ink-muted">
          <Icon size={14} aria-hidden className="shrink-0" />
          <span className="truncate">{title(s.title)}</span>
        </h2>
        <ul className="m-0 flex list-none flex-col gap-0.5 p-0">
          {s.routes.map((r) => (
            <li key={r.path}>
              <NavLink to={workspace?.href(r.path) ?? r.path} className={cn(item, 'pl-8', touch && 'h-10')}>
                <span className="truncate" title={title(r.title)}>
                  {title(r.title)}
                </span>
              </NavLink>
            </li>
          ))}
        </ul>
      </section>
    );
  });
}

/**
 * Left navigation: every section with its screens at once (no hidden levels), the current screen
 * highlighted; collapses to an icon rail. Phones use NavDrawer instead.
 */
export function Sidebar({ collapsed }: { collapsed: boolean }) {
  const { t } = useTranslation('shell');
  const menu = useMenu();
  return (
    <aside className={cn('flex h-full shrink-0 flex-col border-r border-line bg-subtle', collapsed ? 'w-16' : 'w-60')}>
      <nav aria-label={t('header.mainNav')} className="min-h-0 flex-1 overflow-y-auto px-3 py-3">
        {collapsed ? (
          <div className="flex flex-col items-center gap-1">
            {menu.map((s) => (
              <RailSection key={s.key} section={s} />
            ))}
          </div>
        ) : (
          <SectionList />
        )}
      </nav>
    </aside>
  );
}

/**
 * The navigation on phones: a hamburger in the header opening the full section menu from the
 * left (same sections, permissions and current page as the sidebar). Closes once a screen opens.
 */
export function NavDrawer() {
  const { t } = useTranslation('shell');
  const [open, setOpen] = useState(false);
  const { pathname, search } = useLocation();
  const at = pathname + search;
  const [openedAt, setOpenedAt] = useState(at);
  if (open && openedAt !== at) {
    // navigated (a menu link, or back/forward): the drawer has done its job
    setOpen(false);
  }
  return (
    <>
      <IconButton
        label={t('header.openMenu')}
        icon={<Menu />}
        size="lg"
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => {
          setOpenedAt(at);
          setOpen(true);
        }}
        className="-ml-1"
      />
      <Sheet open={open} onOpenChange={setOpen} side="left" title={t('header.menu')} bodyClassName="px-2 py-3">
        <nav aria-label={t('header.mainNav')} onClick={(e) => (e.target as Element).closest?.('a') && setOpen(false)}>
          <SectionList touch />
        </nav>
      </Sheet>
    </>
  );
}
